import React, { useState, useEffect } from "react"
import { HexColorInput, RgbaColorPicker } from "react-colorful"
import {
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
  UncontrolledPopover,
} from "reactstrap"
import { convertRgbaToHex } from "utils"
import "components/style.scss"

const ColorPicker = ({
  input = {},
  label,
  meta: { touched, error },
  disabled = false,
  onClick= () => {}
}) => {
  const [selectedColor, setSelectedColor] = useState(null)
  const pickerId = (input.id ? input.id : `${input.name}-colorPicker`)
    .replaceAll(".", "")
    .replaceAll("[", "")
    .replaceAll("]", "")
  let timeOut

  const convertHexToRGB = hexValue => {
    let r = 0,
      g = 0,
      b = 0

    if (hexValue.length == 4) {
      r = "0x" + hexValue[1] + hexValue[1]
      g = "0x" + hexValue[2] + hexValue[2]
      b = "0x" + hexValue[3] + hexValue[3]
    } else if (hexValue.length == 7) {
      r = "0x" + hexValue[1] + hexValue[2]
      g = "0x" + hexValue[3] + hexValue[4]
      b = "0x" + hexValue[5] + hexValue[6]
    }

    return { r: parseInt(r, 16), g: parseInt(g, 16), b: parseInt(b, 16) }
  }

  useEffect(() => {
    if (input.value && !selectedColor) {
      if (input.value?.includes("rgb")) {
        const values = input.value?.split("(")[1]?.split(")")[0]?.split(",")
        if (values) {
          setSelectedColor({
            r: parseInt(values[0] || 0),
            g: parseInt(values[1] || 0),
            b: parseInt(values[2] || 0),
            a: isNaN(parseFloat(values[3])) ? 1 : parseFloat(values[3]),
          })
        }
      } else {
        let { r, g, b } = convertHexToRGB(input.value)
        setSelectedColor({ r, g, b, a: 1 })
      }
    }
  }, [input.value])

  const handleChange = color => {
    if (timeOut) {
      clearTimeout(timeOut)
    }
    timeOut = setTimeout(() => {
      setSelectedColor(color)
      input.onChange(
        `rgba(${color?.r || 0},${color?.g || 0},${color?.b || 0},${
          isNaN(parseFloat(color?.a)) ? 1 : parseFloat(color?.a)
        })`
      )
    }, 300)
  }

  const handleManualChange = (value = "") => {
    let { r, g, b } = convertHexToRGB(value)
    setSelectedColor({ r, g, b, a: 1 })
  }

  const handleManualRGBChange = ({ target: { value = "", id = "" } }) => {
    let validValue = 0
    if (id !== "aValue" && value > 255) {
      validValue = 255
    } else if (id === "aValue" && value > 1) {
      validValue = 1
    } else {
      validValue = value
    }
    if (id === "rValue") {
      setSelectedColor({ ...selectedColor, r: parseInt(validValue) })
      input.onChange(
        `rgba(${validValue},${selectedColor?.g || 0},${selectedColor?.b || 0},${
          selectedColor?.a || 0
        })`
      )
    }
    if (id === "gValue") {
      setSelectedColor({ ...selectedColor, g: parseInt(validValue) })
      input.onChange(
        `rgba(${selectedColor?.r || 0},${validValue},${selectedColor?.b || 0},${
          selectedColor?.a || 0
        })`
      )
    }
    if (id === "bValue") {
      setSelectedColor({ ...selectedColor, b: parseInt(validValue) })
      input.onChange(
        `rgba(${selectedColor?.r || 0},${selectedColor?.g || 0},${validValue},${
          selectedColor?.a || 0
        })`
      )
    }
    if (id === "aValue") {
      setSelectedColor({ ...selectedColor, a: parseFloat(validValue) })
      input.onChange(
        `rgba(${selectedColor?.r || 0},${selectedColor?.g || 0},${
          selectedColor?.b || 0
        },${validValue})`
      )
    }
  }

  return (
    <React.Fragment>
      {label && <label>{label}</label>}
      <InputGroup id={pickerId}>
        <InputGroupAddon onClick={onClick} addonType="prepend">
          <span className="bx bxs-color-fill input-group-text" />
        </InputGroupAddon>
        <Input onClick={onClick} value={input.value || "rgba(0,0,0,1)"} readOnly />
        <InputGroupText onClick={onClick}>
          <i
            style={{
              height: "16px",
              width: "16px",
              background: input.value ? `${input.value}` : "rgba(0,0,0,1)",
            }}
          />
        </InputGroupText>
        <UncontrolledPopover
          placement="bottom-start"
          target={pickerId}
          trigger="legacy"
          disabled={disabled}
        >
          <div className="custom-layout">
            <RgbaColorPicker
              color={selectedColor || { r: 0, g: 0, b: 0, a: 1 }}
              onChange={handleChange}
            />
            <Row className="my-2">
              <Col>
                <HexColorInput
                  color={
                    input?.value?.includes("rgb")
                      ? convertRgbaToHex(input?.value)
                      : input?.value
                  }
                  prefixed
                  onChange={handleManualChange}
                  className="form-control-sm form-control"
                  onBlur={() =>
                    input.onChange(
                      `rgba(${selectedColor?.r || 0},${selectedColor?.g || 0},${
                        selectedColor?.b || 0
                      },${
                        isNaN(parseFloat(selectedColor?.a))
                          ? 1
                          : parseFloat(selectedColor?.a)
                      })`
                    )
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col className="pe-0">
                <Label for="rValue">R</Label>
                <Input
                  type="number"
                  id="rValue"
                  value={selectedColor?.r || 0}
                  bsSize="sm"
                  min={0}
                  max={255}
                  onChange={handleManualRGBChange}
                />
              </Col>
              <Col className="p-0">
                <Label for="gValue">G</Label>
                <Input
                  type="number"
                  id="gValue"
                  value={selectedColor?.g || 0}
                  bsSize="sm"
                  min={0}
                  max={255}
                  onChange={handleManualRGBChange}
                />
              </Col>
              <Col className="p-0">
                <Label for="bValue">B</Label>
                <Input
                  type="number"
                  id="bValue"
                  value={selectedColor?.b || 0}
                  bsSize="sm"
                  min={0}
                  max={255}
                  onChange={handleManualRGBChange}
                />
              </Col>
              <Col className="ps-0">
                <Label for="aValue">A</Label>
                <Input
                  type="number"
                  id="aValue"
                  value={selectedColor?.a || 0}
                  bsSize="sm"
                  min={0}
                  max={1}
                  step={0.1}
                  onChange={handleManualRGBChange}
                />
              </Col>
            </Row>
          </div>
        </UncontrolledPopover>
      </InputGroup>
      {touched && error && <InputError error={error} />}
    </React.Fragment>
  )
}

export default ColorPicker
